// src/components/admin/ProductTable.jsx
import React from 'react';
import { Edit2, Trash2, Package, ImageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const ProductTable = ({ products, onEdit, onDelete, onInventory, isReadOnly, categoryTree = [] }) => {
  const { language } = useApp();
  const t = translations[language];

  const getProductName = (product) => {
    if (language === 'en') return product.name_en || product.name;
    if (language === 'km') return product.name_km || product.name;
    return product.name;
  };

  const getCategoryName = (product) => {
    if (language === 'en') return product.category_name_en || product.category_name;
    if (language === 'km') return product.category_name_km || product.category_name;
    return product.category_name;
  };

  const getParentCategoryName = (product) => {
    if (!categoryTree.length || !product.category_id) return null;
    for (const parent of categoryTree) {
      const found = (parent.children || []).find(c => c.id === parseInt(product.category_id));
      if (found) {
        if (language === 'en') return parent.name_en || parent.name;
        if (language === 'km') return parent.name_km || parent.name;
        return parent.name;
      }
    }
    return null;
  };

  const getUnitName = (product) => {
    if (language === 'en') return product.unit_name_en || product.unit_name || product.unit;
    if (language === 'km') return product.unit_name_km || product.unit_name || product.unit;
    return product.unit_name || product.unit;
  };

  const getStockStatus = (product) => {
    const stock = product.stock || 0;
    const minStock = product.min_stock || 0;
    if (stock === 0) return { color: 'bg-red-100 text-red-800', text: language === 'en' ? 'Out of Stock' : '缺货' };
    if (stock <= minStock) return { color: 'bg-orange-100 text-orange-800', text: language === 'en' ? 'Low Stock' : '库存不足' };
    return { color: 'bg-green-100 text-green-800', text: language === 'en' ? 'In Stock' : '有货' };
  };

  if (!products || products.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow text-center py-12">
        <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">{language === 'en' ? 'No products found' : '暂无商品'}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">图片</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">商品</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">分类</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">散装价格</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">📦 箱装</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">库存</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">排序</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => {
              const stockStatus = getStockStatus(product);
              const parentName = getParentCategoryName(product);
              const childName = getCategoryName(product);
              const hasBox = product.box_quantity > 0;

              return (
                <tr key={product.id} className="hover:bg-gray-50">
                  {/* 图片 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {product.image ? (
                        <img src={product.image} alt={getProductName(product)}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </td>

                  {/* 商品信息 */}
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">{getProductName(product)}</div>
                    {product.barcode && <div className="text-xs text-gray-400">{product.barcode}</div>}
                    <div className="text-xs text-gray-400">{getUnitName(product)}</div>
                  </td>

                  {/* 分类 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {parentName ? (
                      <div className="flex flex-col gap-1">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600">{parentName}</span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{childName || '-'}</span>
                      </div>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{childName || '-'}</span>
                    )}
                  </td>

                  {/* 散装价格 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 font-medium">
                      ${parseFloat(product.sale_price || 0).toFixed(2)}
                      <span className="text-xs text-gray-400 ml-1">零售</span>
                    </div>
                    {product.vip_price && (
                      <div className="text-xs text-purple-600 font-medium">
                        ${parseFloat(product.vip_price).toFixed(2)}
                        <span className="ml-1 text-gray-400">VIP</span>
                      </div>
                    )}
                    <div className="text-xs text-gray-400">
                      成本: ${parseFloat(product.cost_price || 0).toFixed(2)}
                    </div>
                  </td>

                  {/* 箱装信息 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {hasBox ? (
                      <div className="text-xs space-y-1">
                        <div className="text-gray-500">1箱 = {product.box_quantity} 个</div>
                        {product.box_price && (
                          <div className="text-orange-600 font-medium">
                            ${parseFloat(product.box_price).toFixed(2)} / 箱
                          </div>
                        )}
                        {product.vip_box_price && (
                          <div className="text-purple-600 font-medium">
                            ${parseFloat(product.vip_box_price).toFixed(2)} VIP
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>

                  {/* 库存 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.stock || 0} 个</div>
                    {hasBox && product.box_quantity && (
                      <div className="text-xs text-gray-400">
                        ≈ {Math.floor((product.stock || 0) / product.box_quantity)} 箱
                        {(product.stock || 0) % product.box_quantity > 0 && ` + ${(product.stock || 0) % product.box_quantity} 个`}
                      </div>
                    )}
                    {product.min_stock > 0 && (
                      <div className="text-xs text-gray-400">最低: {product.min_stock}</div>
                    )}
                  </td>

                  {/* 排序权重 */}
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    {product.sort_order != null
                      ? <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">{product.sort_order}</span>
                      : <span className="text-xs text-gray-300">—</span>
                    }
                  </td>

                  {/* 状态 */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1.5">
                      {/* 库存状态 */}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full w-fit ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                      {/* 三个显示开关状态 */}
                      <div className="flex gap-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          product.show_in_pos !== 0 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-300 line-through'
                        }`}>收银台</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          product.show_in_h5 !== 0 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-300 line-through'
                        }`}>网店</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          product.show_in_transfer !== 0 ? 'bg-orange-50 text-orange-600' : 'bg-gray-100 text-gray-300 line-through'
                        }`}>调拨</span>
                      </div>
                    </div>
                  </td>

                  {/* 操作 */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      {onEdit && (
                        <button onClick={() => onEdit(product)}
                          className="text-blue-600 hover:text-blue-800 transition-colors" title={t.edit}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => onInventory(product)}
                        className="text-green-600 hover:text-green-800 transition-colors" title="调整库存">
                        <Package className="w-4 h-4" />
                      </button>
                      {onDelete && (
                        <button onClick={() => onDelete(product.id)}
                          className="text-red-600 hover:text-red-800 transition-colors" title={t.delete}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;